import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminCompanyContact.css";

const EMPTY_FORM = {
  phone_number:           "",
  email_address:          "",
  physical_address:       "",
  physical_address_link:  "",
  address_iframe_link:    "",
};

/* ─────────────────────────────────
   Field Row — label + input/textarea
   ───────────────────────────────── */
function FieldRow({ label, hint, required, children }) {
  return (
    <div className="acc-field-row">
      <div className="acc-field-row__label-wrap">
        <span className="acc-field-row__label">
          {label}
          {required && <span className="acc-req">*</span>}
        </span>
        {hint && <span className="acc-field-row__hint">{hint}</span>}
      </div>
      <div className="acc-field-row__control">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────
   Preview Card — read-only summary
   ───────────────────────────────── */
function PreviewCard({ contact }) {
  if (!contact) return null;

  const rows = [
    { icon: "📞", label: "Phone",   val: contact.phone_number  },
    { icon: "✉",  label: "Email",   val: contact.email_address },
    { icon: "📍", label: "Address", val: contact.physical_address },
    {
      icon: "🗺",
      label: "Map Link",
      val: contact.physical_address_link
        ? <a href={contact.physical_address_link} target="_blank" rel="noreferrer">
            {contact.physical_address_link}
          </a>
        : null,
    },
  ];

  return (
    <div className="acc-preview">
      <div className="acc-preview__header">
        <span className="acc-preview__tag">LIVE DATA</span>
        <span className="acc-preview__title">Current Contact Info</span>
      </div>
      <div className="acc-preview__body">
        {rows.map(({ icon, label, val }) => (
          <div key={label} className="acc-preview__row">
            <span className="acc-preview__icon">{icon}</span>
            <div className="acc-preview__text">
              <span className="acc-preview__row-label">{label}</span>
              <span className="acc-preview__row-val">
                {val || <span className="acc-preview__empty">Not set</span>}
              </span>
            </div>
          </div>
        ))}
        {contact.updated_at && (
          <div className="acc-preview__updated">
            Last updated:{" "}
            {new Date(contact.updated_at).toLocaleString("en-ZA", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminCompanyContact() {
  const [contact,  setContact]  = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");
  const [dirty,    setDirty]    = useState(false);

  /* ── Load contact ── */
  useEffect(() => {
    apiFetch("/api/admin/company-contact")
      .then((data) => {
        setContact(data);
        setForm({
          phone_number:          data.phone_number          || "",
          email_address:         data.email_address         || "",
          physical_address:      data.physical_address      || "",
          physical_address_link: data.physical_address_link || "",
          address_iframe_link:   data.address_iframe_link   || "",
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setDirty(true);
    setSuccess("");
  };

  const handleReset = () => {
    if (!contact) return;
    setForm({
      phone_number:          contact.phone_number          || "",
      email_address:         contact.email_address         || "",
      physical_address:      contact.physical_address      || "",
      physical_address_link: contact.physical_address_link || "",
      address_iframe_link:   contact.address_iframe_link   || "",
    });
    setDirty(false);
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      const updated = await apiFetch("/api/admin/company-contact", {
        method:  "PUT",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          phone_number:          form.phone_number.trim()          || null,
          email_address:         form.email_address.trim()         || null,
          physical_address:      form.physical_address.trim()      || null,
          physical_address_link: form.physical_address_link.trim() || null,
          address_iframe_link:   form.address_iframe_link.trim()   || null,
        }),
      });
      setContact(updated);
      setDirty(false);
      setSuccess("Company contact details saved successfully.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Skeleton ── */
  if (loading) {
    return (
      <AdminLayout pageTag="SETTINGS" pageTitle="Company Contact">
        <div className="acc-skeleton" />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTag="SETTINGS" pageTitle="Company Contact">

      {/* ── Page intro bar ── */}
      <div className="acc-intro-bar">
        <div className="acc-intro-bar__left">
          <span className="acc-intro-bar__icon">🏢</span>
          <div>
            <h2 className="acc-intro-bar__title">Company Contact Details</h2>
            <p className="acc-intro-bar__sub">
              These details are displayed across the website — header, footer, and contact page.
            </p>
          </div>
        </div>
        {dirty && (
          <span className="acc-intro-bar__unsaved">● Unsaved changes</span>
        )}
      </div>

      {/* ── Alerts ── */}
      {error   && <div className="acc-alert acc-alert--error">⚠ {error}</div>}
      {success && <div className="acc-alert acc-alert--success">✓ {success}</div>}

      {/* ── Main layout: form + preview ── */}
      <div className="acc-layout">

        {/* ── Form panel ── */}
        <div className="acc-form-panel">

          {/* Section: Contact Info */}
          <div className="acc-section">
            <div className="acc-section__head">
              <h3 className="acc-section__title">Contact Information</h3>
              <p className="acc-section__desc">Primary phone and email shown in the header and footer.</p>
            </div>

            <FieldRow label="Phone Number" hint="Include country code e.g. +27 31 000 0000">
              <input
                type="text"
                className="acc-input"
                value={form.phone_number}
                onChange={(e) => set("phone_number", e.target.value)}
                placeholder="+27 31 000 0000"
              />
            </FieldRow>

            <FieldRow label="Email Address" hint="Primary business enquiry email">
              <input
                type="email"
                className="acc-input"
                value={form.email_address}
                onChange={(e) => set("email_address", e.target.value)}
                placeholder="info@yourbusiness.co.za"
              />
            </FieldRow>
          </div>

          {/* Section: Physical Address */}
          <div className="acc-section">
            <div className="acc-section__head">
              <h3 className="acc-section__title">Physical Address</h3>
              <p className="acc-section__desc">Shown on the contact page and footer. Include the full street address.</p>
            </div>

            <FieldRow label="Street Address" hint="Full address as it should appear on site">
              <textarea
                className="acc-textarea"
                rows={3}
                value={form.physical_address}
                onChange={(e) => set("physical_address", e.target.value)}
                placeholder={"123 Main Road\nDurban, KwaZulu-Natal\n4001"}
              />
            </FieldRow>

            <FieldRow
              label="Google Maps Link"
              hint="Paste the Google Maps share URL — used for the 'Get Directions' button"
            >
              <input
                type="url"
                className="acc-input"
                value={form.physical_address_link}
                onChange={(e) => set("physical_address_link", e.target.value)}
                placeholder="https://maps.google.com/?q=..."
              />
              {form.physical_address_link && (
                <a
                  href={form.physical_address_link}
                  target="_blank"
                  rel="noreferrer"
                  className="acc-link-preview"
                >
                  ↗ Open in Google Maps
                </a>
              )}
            </FieldRow>
          </div>

          {/* Section: Map Embed */}
          <div className="acc-section acc-section--last">
            <div className="acc-section__head">
              <h3 className="acc-section__title">Map Embed</h3>
              <p className="acc-section__desc">
                Paste the full <code>&lt;iframe&gt;</code> embed code from Google Maps to show an
                interactive map on the contact page.
              </p>
            </div>

            <FieldRow
              label="Iframe Embed Code"
              hint='Go to Google Maps → Share → Embed a map → Copy the full <iframe> tag'
            >
              <textarea
                className="acc-textarea acc-textarea--code"
                rows={5}
                value={form.address_iframe_link}
                onChange={(e) => set("address_iframe_link", e.target.value)}
                placeholder={'<iframe src="https://www.google.com/maps/embed?..." width="600" height="450" ...></iframe>'}
                spellCheck={false}
              />
            </FieldRow>

            {/* Live iframe preview */}
            {form.address_iframe_link && form.address_iframe_link.includes("<iframe") && (
              <div className="acc-iframe-preview">
                <span className="acc-iframe-preview__label">Map Preview</span>
                <div
                  className="acc-iframe-preview__frame"
                  dangerouslySetInnerHTML={{ __html: form.address_iframe_link }}
                />
              </div>
            )}
          </div>

          {/* ── Footer actions ── */}
          <div className="acc-form-footer">
            <button
              className="acc-btn acc-btn--cancel"
              onClick={handleReset}
              disabled={saving || !dirty}
            >
              Discard Changes
            </button>
            <button
              className="acc-btn acc-btn--save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>

        {/* ── Preview panel ── */}
        <div className="acc-preview-panel">
          <PreviewCard contact={contact} />

          <div className="acc-tips">
            <h4 className="acc-tips__title">💡 Tips</h4>
            <ul className="acc-tips__list">
              <li>Changes take effect immediately on the live site after saving.</li>
              <li>The phone number is used for click-to-call links — use international format.</li>
              <li>The Google Maps iframe must come directly from the Google Maps embed tool.</li>
              <li>Leave a field blank to hide it from the website.</li>
            </ul>
          </div>
        </div>

      </div>
    </AdminLayout>
  );
}